/**
 * Record Pages Hooks
 *
 * React Query hooks for managing record page assignments.
 * Provides CRUD operations and smart assignment logic based on application context.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { persistedRecordPages } from '../services/mockData/recordPages';
import { RecordPage } from '../types/recordPages';

// ============================================
// QUERY KEYS
// ============================================

export const recordPageKeys = {
  all: ['recordPages'] as const,
  byObject: (objectName: string) => [...recordPageKeys.all, 'object', objectName] as const,
  byApplication: (objectName: string, application: string) =>
    [...recordPageKeys.byObject(objectName), 'app', application] as const,
  assignment: (objectName: string, context: any) =>
    [...recordPageKeys.all, 'assignment', objectName, JSON.stringify(context)] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all record pages for an object
 * Optionally filter by application
 */
/** Strip Gobodo env suffix for object name comparison (e.g. Client__stg3_c → client) */
function normalizeObjectName(name: string): string {
  return name.replace(/(__stg3_c|__stg_c|__c)$/i, '').toLowerCase();
}

export function useRecordPages(objectName: string, application?: string) {
  return useQuery({
    queryKey: application
      ? recordPageKeys.byApplication(objectName, application)
      : recordPageKeys.byObject(objectName),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));

      const allRecordPages = persistedRecordPages.get();
      const normalizedInput = normalizeObjectName(objectName);

      // Debug logging
      console.log('📦 useRecordPages Query:');
      console.log('  Querying for objectName:', objectName, '(normalized:', normalizedInput, ')');
      console.log('  Application filter:', application);
      console.log('  Total record pages in storage:', allRecordPages.length);
      console.log('  All object names:', Array.from(new Set(allRecordPages.map(rp => rp.objectName))));

      // Normalize both sides to handle env suffixes (Client__stg3_c vs Client__c)
      let filtered = allRecordPages.filter(rp => normalizeObjectName(rp.objectName) === normalizedInput);
      console.log('  After objectName filter:', filtered.length);

      if (application && application !== 'all') {
        filtered = filtered.filter(rp => rp.application === application);
        console.log('  After application filter:', filtered.length);
      }

      return filtered.sort((a, b) => b.priority - a.priority);
    },
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Get single record page by ID
 */
export function useRecordPage(id: string) {
  return useQuery({
    queryKey: [...recordPageKeys.all, 'detail', id],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));

      const allRecordPages = persistedRecordPages.get();
      const recordPage = allRecordPages.find(rp => rp.id === id);

      if (!recordPage) {
        throw new Error('Record page not found');
      }

      return recordPage;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!id
  });
}

/**
 * Get assigned page layout for a record based on context
 * This is the core assignment logic that determines which layout to show
 *
 * @param objectName - The object type (e.g., 'Lead', 'Client')
 * @param context - Context for assignment including application and record data
 * @returns The matching RecordPage with highest priority
 */
export function useRecordPageAssignment(
  objectName: string,
  context: {
    application?: string;
    record?: any;
  }
) {
  return useQuery({
    queryKey: recordPageKeys.assignment(objectName, context),
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));

      const allRecordPages = persistedRecordPages.get();
      const normalizedInput = normalizeObjectName(objectName);
      const objectPages = allRecordPages.filter(rp =>
        normalizeObjectName(rp.objectName) === normalizedInput && rp.isActive
      );

      // Sort by priority (highest first)
      const sorted = objectPages.sort((a, b) => b.priority - a.priority);

      // Find matching record page based on context
      for (const recordPage of sorted) {
        // Check application match
        if (context.application) {
          // Exact application match has higher priority than Global
          if (recordPage.application !== context.application && recordPage.application !== 'Global') {
            continue;
          }
        }

        // Check conditions match (if record data is provided)
        if (recordPage.conditions && recordPage.conditions.length > 0 && context.record) {
          const allConditionsMet = recordPage.conditions.every(condition => {
            const fieldValue = context.record[condition.field];

            switch (condition.operator) {
              case 'equals':
                return fieldValue === condition.value;
              case 'notEquals':
                return fieldValue !== condition.value;
              case 'contains':
                return String(fieldValue).includes(String(condition.value));
              case 'in':
                return Array.isArray(condition.value) && condition.value.includes(fieldValue);
              default:
                return false;
            }
          });

          if (!allConditionsMet) {
            continue;
          }
        }

        // Found a match!
        return recordPage;
      }

      // Fallback to default or first available
      return sorted.find(rp => rp.isDefault) || sorted[0] || null;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!objectName
  });
}

/**
 * Create or update a record page
 */
export function useSaveRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recordPage: Partial<RecordPage> & { id?: string }) => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const allRecordPages = persistedRecordPages.get();

      if (recordPage.id) {
        // Update existing record page
        const index = allRecordPages.findIndex(rp => rp.id === recordPage.id);

        if (index === -1) {
          throw new Error('Record page not found');
        }

        const updated: RecordPage = {
          ...allRecordPages[index],
          ...recordPage,
          lastModified: new Date().toISOString()
        } as RecordPage;

        const updatedList = [...allRecordPages];
        updatedList[index] = updated;
        persistedRecordPages.set(updatedList);

        return updated;
      } else {
        // Create new record page
        const newRecordPage: RecordPage = {
          id: `rp_${Date.now()}`,
          name: recordPage.name!,
          objectName: recordPage.objectName!,
          application: recordPage.application || 'Global',
          pageLayoutId: recordPage.pageLayoutId!,
          priority: recordPage.priority || 1,
          isActive: recordPage.isActive ?? true,
          createdDate: new Date().toISOString(),
          lastModified: new Date().toISOString(),
          ...recordPage
        };

        persistedRecordPages.set([...allRecordPages, newRecordPage]);

        return newRecordPage;
      }
    },
    onSuccess: (data) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: recordPageKeys.byObject(data.objectName) });
      queryClient.invalidateQueries({ queryKey: recordPageKeys.assignment(data.objectName, {}) });
    }
  });
}

/**
 * Delete a record page
 */
export function useDeleteRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const allRecordPages = persistedRecordPages.get();
      const recordPage = allRecordPages.find(rp => rp.id === id);

      if (!recordPage) {
        throw new Error('Record page not found');
      }

      // Don't allow deleting default record pages
      if (recordPage.isDefault) {
        throw new Error('Cannot delete default record page');
      }

      const filtered = allRecordPages.filter(rp => rp.id !== id);
      persistedRecordPages.set(filtered);

      return { id, objectName: recordPage.objectName };
    },
    onSuccess: (data) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: recordPageKeys.byObject(data.objectName) });
      queryClient.invalidateQueries({ queryKey: recordPageKeys.assignment(data.objectName, {}) });
    }
  });
}

/**
 * Clone a record page
 */
export function useCloneRecordPage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await new Promise(resolve => setTimeout(resolve, 300));

      const allRecordPages = persistedRecordPages.get();
      const original = allRecordPages.find(rp => rp.id === id);

      if (!original) {
        throw new Error('Record page not found');
      }

      const cloned: RecordPage = {
        ...original,
        id: `rp_${Date.now()}`,
        name: `${original.name} (Copy)`,
        isDefault: false,
        createdDate: new Date().toISOString(),
        lastModified: new Date().toISOString()
      };

      persistedRecordPages.set([...allRecordPages, cloned]);

      return cloned;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: recordPageKeys.byObject(data.objectName) });
    }
  });
}
